// Home cookbook picker and a compact view of personal cooking progress.
import React, { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { COUNTRY_COOKBOOKS, CONTENT_RECIPES } from '../../content';
import { useDiet, useToday } from '../hooks';
import { activeDays, computeStreak, cookbookProgress, repairableDays } from '../logic/streak';
import { useProgress } from '../store/progress';
import { useSettings } from '../store/settings';
import { useNav } from '../navigation';
import { DietPlanStreakRow } from './DietPlanCard';
import Icon from './Icon';
import Photo from './Photo';
import Sheet from './Sheet';
import { notify } from './Dialogs';
import { Button, Caption, ProgressBar, Row } from './ui';
import { font, radius, spacing, usePalette } from '../theme';

/** The cookbook the home screen follows: the one you picked, or the first one. */
export function useCurrentCookbook() {
  const picked = useProgress(s => s.pinnedCookbookId);
  return COUNTRY_COOKBOOKS.find(c => c.id === picked) ?? COUNTRY_COOKBOOKS[0];
}

/** Your own streak, derived from photo-logged meals (logic/streak.ts). */
export function usePersonalStreak() {
  const today = useToday();
  const isSuper = useSettings(s => s.isSuper);
  const { log, coveredDays } = useProgress();
  const active = useMemo(() => activeDays(log), [log]);
  const covered = useMemo(() => new Set(coveredDays), [coveredDays]);
  const streak = computeStreak(active, covered, today);
  return { ...streak, canRepair: isSuper && !streak.alive && repairableDays(active, covered, today).length > 0 };
}

/** The flag button (top left) and the streak number (top right). */
export function FlagButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation();
  const book = useCurrentCookbook();
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={t('home.pickCookbook', { name: book?.title ?? '' })} onPress={onPress} hitSlop={8} style={{ paddingHorizontal: spacing.s }}>
      <Text style={{ fontSize: 28 }}>{book?.flag ?? '📖'}</Text>
    </Pressable>
  );
}
export function StreakButton({ count, lit, onPress }: { count: number; lit: boolean; onPress: () => void }) {
  const { t } = useTranslation();
  const p = usePalette();
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={t('home.streakButton', { count })} onPress={onPress} hitSlop={8}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.s }}>
      <Icon name="streak" size={22} color={lit ? p.streak : p.secondaryText} />
      <Text style={{ fontSize: font.headline, fontWeight: '800', color: lit ? p.streak : p.secondaryText }}>{count}</Text>
    </Pressable>
  );
}

/** Every cookbook with its flag and progress; picking one makes it the home screen's path. */
export function CookbookPickerSheet({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const p = usePalette();
  const diet = useDiet();
  const isSuper = useSettings(s => s.isSuper);
  const { log, pin } = useProgress();
  const current = useCurrentCookbook();
  return (
    <Sheet visible={visible} onClose={onClose} title={t('home.cookbooks')}>
      {COUNTRY_COOKBOOKS.map(book => {
        const ids = book.sections.flatMap(s => s.recipeIds).filter(id => CONTENT_RECIPES[id] && !diet.hidden(CONTENT_RECIPES[id]));
        const progress = cookbookProgress(ids, log);
        const locked = book.premium && !isSuper;
        const selected = book.id === current?.id;
        return (
          <Pressable key={book.id} accessibilityRole="button" accessibilityState={{ selected }} onPress={() => { pin(book.id); onClose(); }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.m, padding: spacing.m, borderRadius: radius.l, backgroundColor: selected ? p.card : 'transparent' }}>
            <Text style={{ fontSize: 32 }}>{book.flag}</Text>
            <View style={{ flex: 1, gap: 4 }}>
              <Text style={{ fontSize: font.body, fontWeight: '700', color: p.text }}>{book.title}</Text>
              <ProgressBar value={progress.total ? progress.cooked / progress.total : 0} color={locked ? p.super : p.success} />
            </View>
            <Caption>{progress.cooked}/{progress.total}</Caption>
            {book.premium ? <Icon name={locked ? 'lock' : 'super'} size={18} color={p.super} /> : null}
          </Pressable>
        );
      })}
    </Sheet>
  );
}

/** Personal cooking results; friendship streaks live directly on conversations. */
export function StreakSheet({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const today = useToday();
  const isSuper = useSettings(s => s.isSuper);
  const { log, freezes, repair } = useProgress();
  const streak = usePersonalStreak();
  const go = (fn: () => void) => { onClose(); fn(); };
  return (
    <Sheet visible={visible} onClose={onClose} title="">
      <View style={{ gap: spacing.m }}>
        <>
            <Row gap={spacing.m} style={{ flexWrap: 'nowrap' }}>
              <View style={{ backgroundColor: streak.todayDone ? p.streak : p.border, borderRadius: radius.pill, padding: spacing.s }}>
                <Icon name="streak" size={28} color={streak.todayDone ? p.onTint : p.secondaryText} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: font.title2, fontWeight: '800', color: p.text }}>
                  {streak.current > 0 ? t('path.streak', { count: streak.current }) : t('path.noStreak')}
                </Text>
              </View>
            </Row>
            <Row gap={spacing.m}>
              <Caption>{t('path.best', { count: streak.best })}</Caption>
              {isSuper ? <Row gap={4}><Icon name="freeze" size={16} color={p.info} /><Caption>{t('path.freezes', { count: freezes })}</Caption></Row> : null}
            </Row>
            {streak.canRepair ? <Button label={t('path.repair')} icon="reset" variant="super" onPress={() => { if (repair(today)) notify(t('path.repaired')); }} /> : null}
            <DietPlanStreakRow />
        </>
        <Button label={t('path.logMeal')} icon="camera" big onPress={() => go(() => nav.navigate('LogMeal', {}))} />
        {log.length > 0 ? (
          <Pressable accessibilityRole="button" onPress={() => go(() => nav.navigate('MealDiary'))} style={{ gap: spacing.s }}>
            <Row>
              <Text style={{ flex: 1, fontSize: font.body, fontWeight: '600', color: p.text }}>{t('path.diary')}</Text>
              <Icon name="forward" size={18} color={p.secondaryText} />
            </Row>
            <Row style={{ flexWrap: 'nowrap' }}>
              {log.slice(0, 5).map(e => <Photo key={e.id} media={e.photo} seed={e.id} icon="meal" style={{ width: 48, height: 48 }} iconSize={18} />)}
            </Row>
          </Pressable>
        ) : null}
        <Button label={t('home.openSocial')} icon="buddies" variant="secondary" onPress={() => go(() => nav.navigate('Tabs', { screen: 'SocialTab' }))} />
      </View>
    </Sheet>
  );
}

